import { useEffect, useRef, useCallback, useState } from 'react';
import { useToast } from '@/hooks/use-toast';

export const useAdminNotifications = (needsHumanCount: number) => {
  const { toast } = useToast();
  const previousCountRef = useRef(needsHumanCount);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  // Check and request notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        setHasPermission(true);
      } else if (Notification.permission === 'denied') {
        setPermissionDenied(true);
      }
    }

    // Create audio element for notification sound
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQBRwuPQHD/lxXZN' +
      'l9iUXwBXo8GYQBiduJlcElec1KpCGYrBhVgcWbHGfjEsiryKVxxbtstxKjyXwIZZIWO+x3EpP5vCiFkhY8LIdCdCncWLWiFkxMt3JkSfyI5aIWXHzXomRaLLkVoh' +
      'ZsnQfSZGosuUWyFmytJ+JUajzZZbIGbL1IAkR6TNl1wgZ8zWgSNIpc6YXSBozNeBIkmm0JldH2nN2IMhSqfRml4fac7ZhCBLqNKbXh9q0NqFH0yp051fH2rR24Yf' +
      'TarUnV8fatLchh5OqtWeYB5r09yHHU+r1p9gHWzU3YgdUKzXoGAdbNXeiB1RrdihYB1t1t+IHFKu2aFgHG3X4IkcU6/aoWAcbtjhiRxUr9uhYRxt2eKJG1Ww3KJh' +
      'HG7a4okbVrHdol0fbtvjiRxXst6jXh9v3OSJG1iz36NeH2/d5YkbWbTgo14fb97miRpateGkXh9w3+aJGlu24qVeH3Dg54kaXLfjpV4fcOHniBldu+SmXh9x4uiI' +
      'GV6846ZeH3Hj6YgYX7/kpl4fceToiBhgwOWmXh9y5emIF2HB5qZeH3Lm6ogWYsLnpl0fcufriRZjw+imXR9z6OuJFWTE6aZdH3Pp7IkVZcXqpl0fc+rtiRRmxuqm' +
      'XR906+6JE2fH66ZdH3Ts74kSaMjsplwfdO3wiRFpye2mXB917fGJEGrK7qZcH3Xu8okPa8vvplwfdu/ziQ5szPCmWx927/SJDm3N8aZbH3fw9YkNbs7yplsfeO/2' +
      'iQxvz/OmWx948feJC3DQ9KZbH3nx+IkKcdH1plsfeZD5iQly0vamWx95kfqJCHPT96ZbH3qR+4kHdNT4plsfe5L8iQZ11fmmWx98k/2JBXXX+qZaH3yU/okEdtj7' +
      'pVofe5X/iAN32fymWh98lgCKAnna/aVaH3yXAYoBedv+pVoffJgCigB63/+lWh99mQOJ/3vgAKVaH36aBIn+fOEBpVoffpoFif174gKlWR9/mwaJ/HzjA6RZH3+c' +
      'B4j8feQEpFkfgJ0IiPt+5QWkWR+AnQmI+n/mBqNZH4CeCoj5gOcHo1kfgZ8Lifh/6AijWB+BoAuJ937pCaNYH4KhDIj2f+oKolcfgqINiPV/6wuiVx+Dow6I9IDt' +
      'DKFXH4OkD4fzge0NoVYfhKUQh/KB7w6hVh+Ephf4');
    
    return () => {
      if (audioRef.current) {
        audioRef.current = null;
      }
    };
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setHasPermission(true);
        setPermissionDenied(false);
        toast({
          title: "Notifications enabled! 🔔",
          description: "You'll receive alerts when users request human help.",
        });
      } else if (permission === 'denied') {
        setPermissionDenied(true);
        toast({
          title: "Notifications blocked",
          description: "Please enable notifications in your browser settings.",
          variant: "destructive",
        });
      }
    }
  }, [toast]);

  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 0.5;
      audioRef.current.play().catch(() => {
        // Audio play failed, likely due to browser restrictions
      });
    }
  }, []);

  const showBrowserNotification = useCallback((title: string, body: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'support-request',
        requireInteraction: true,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto-close after 10 seconds
      setTimeout(() => notification.close(), 10000);
    }
  }, []);

  // Watch for new support requests
  useEffect(() => {
    if (needsHumanCount > previousCountRef.current) {
      const newRequests = needsHumanCount - previousCountRef.current;
      
      // Play sound
      playNotificationSound();
      
      // Show browser notification
      showBrowserNotification(
        '🆘 New Support Request',
        `${newRequests} new conversation${newRequests > 1 ? 's' : ''} need${newRequests === 1 ? 's' : ''} human response`
      );
      
      // Show toast
      toast({
        title: '🆘 New Support Request',
        description: `${newRequests} new conversation${newRequests > 1 ? 's' : ''} need${newRequests === 1 ? 's' : ''} attention`,
      });
    }
    
    previousCountRef.current = needsHumanCount;
  }, [needsHumanCount, playNotificationSound, showBrowserNotification, toast]);

  return {
    requestNotificationPermission,
    hasNotificationPermission: hasPermission,
    notificationsDenied: permissionDenied,
    notificationsSupported: 'Notification' in window,
  };
};
